import { useEffect, useState } from 'react'
import { summariseTranscript } from '@/lib/api'
import { composeTranscriptText, formatElapsed } from '@/lib/voiceNotes'
import { useBranding } from '@/contexts/BrandingContext'
import { Textarea } from '@/components/ui/textarea'
import { Spinner } from '@/components/Spinner'

// Preview of a finished voice-notes recording: the transcription AND an
// AI-generated summary, each editable, with explicit copy about what happens
// next — the earlier single-textarea version left operators unsure whether
// anything more was expected of them. The summary is generated automatically on
// open (platform LLM, same adapter as chat); the note itself is still written
// by the chat AI under the selected note style once the message is sent.

interface TranscriptModalProps {
  transcript: string
  durationSec: number
  onAttach: (finalText: string) => void
  onDiscard: () => void
}

export function TranscriptModal({ transcript, durationSec, onAttach, onDiscard }: TranscriptModalProps) {
  const branding = useBranding()
  const [text, setText] = useState(transcript)
  const [summary, setSummary] = useState('')
  const [recapLoading, setRecapLoading] = useState(true)
  const [recapError, setRecapError] = useState<string | null>(null)

  // Summarise automatically — the summary is part of what this modal is FOR,
  // not an optional extra. Runs once against the original transcript.
  useEffect(() => {
    let live = true
    summariseTranscript(transcript)
      .then((res) => { if (live) setSummary(res.summary) })
      .catch(() => {
        if (live) setRecapError('A summary could not be generated — you can still attach the transcript, and the assistant will summarise it when writing the note.')
      })
      .finally(() => { if (live) setRecapLoading(false) })
    return () => { live = false }
  }, [transcript])

  const handleDiscard = () => {
    if (window.confirm('Discard this transcript? The recording is not kept.')) onDiscard()
  }

  const canAttach = (text.trim().length > 0 || summary.trim().length > 0) && !recapLoading

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-card shadow-lg">
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-[15px] font-semibold text-foreground">Transcription &amp; summary</h2>
            <span className="text-[12px] text-muted-foreground">{formatElapsed(durationSec)} recorded</span>
          </div>
          <p className="mt-0.5 text-[12.5px] text-muted-foreground">
            Here is the transcription and summary of your conversation. Review or edit either, then attach —
            the assistant will write it up as a note in your selected style.
          </p>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4">
          <label className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground" htmlFor="voice-summary">
            Summary
          </label>
          {recapLoading ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-3 text-muted-foreground">
              <Spinner size={14} className="text-current" />
              <span className="text-[13px] italic">Summarising the conversation…</span>
            </div>
          ) : recapError ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">{recapError}</p>
          ) : (
            <Textarea
              id="voice-summary"
              className="min-h-24 resize-none text-[14px] leading-relaxed"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              aria-label="Summary"
            />
          )}

          <label className="mt-2 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground" htmlFor="voice-transcript">
            Transcript
          </label>
          <Textarea
            id="voice-transcript"
            className="min-h-40 flex-1 resize-none text-[14px] leading-relaxed"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Transcript"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
          <button
            type="button"
            className="rounded-xl px-3 py-2 text-[13px] font-medium text-muted-foreground"
            onClick={handleDiscard}
          >
            Discard
          </button>
          <span className="flex-1" />
          <button
            type="button"
            className="rounded-xl px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40"
            style={{ backgroundColor: branding.primaryColor }}
            onClick={() => onAttach(composeTranscriptText(summary, text))}
            disabled={!canAttach}
          >
            Attach to chat
          </button>
        </div>
      </div>
    </div>
  )
}
